import { Inject, Injectable, Optional, PLATFORM_ID, REQUEST } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

interface StoredUtmData {
  params: Record<string, string>;
  timestamp: number;
}

@Injectable({
  providedIn: 'root'
})
export class UtmService {
  private utmParams: Record<string, string> = {};
  private readonly STORAGE_KEY = 'utm_params';
  private readonly TIMESTAMP_KEY = 'utm_params_timestamp';
  private readonly SESSION_DURATION = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

  constructor(
    @Inject(PLATFORM_ID) private platformId: object,
    @Optional() @Inject(REQUEST) private request: any, // Optional - only available on server
  ) {
  }

  public captureUtmParams(): void {
    let utmData: Record<string, string> = {};

    // Always check current URL first (prioritize fresh params)
    // On server-side, try to get from REQUEST
    if (!isPlatformBrowser(this.platformId) && this.request && this.request.query) {
      utmData = this.extractTrackingParams(this.request.query);
    } 
    // On browser-side, always read from window.location.search
    if (isPlatformBrowser(this.platformId)) {
      const params = new URLSearchParams(window.location.search);
      params.forEach((value, key) => {
        // Capture UTM parameters (utm_source, utm_medium, etc.)
        if (key.startsWith('utm_')) {
          utmData[key] = value;
        }
        // Capture Google Ads parameters (gclid, gad_source, gad_campaignid, etc.)
        else if (key === 'gclid' || key.startsWith('gad_')) {
          utmData[key] = value;
        }
        // Capture Facebook Click ID (fbclid)
        else if (key === 'fbclid') {
          utmData[key] = value;
        }
      });
    }

    // If we found UTM params in current URL, use them and store with timestamp
    if (Object.keys(utmData).length > 0) {
      this.utmParams = utmData;
      if (isPlatformBrowser(this.platformId)) {
        const now = Date.now();
        localStorage.setItem(this.STORAGE_KEY, JSON.stringify(utmData));
        localStorage.setItem(this.TIMESTAMP_KEY, now.toString());
      }
    } else if (isPlatformBrowser(this.platformId)) {
      // No UTM params in current URL - check if we have valid stored params from same session
      const stored = localStorage.getItem(this.STORAGE_KEY);
      const storedTimestamp = localStorage.getItem(this.TIMESTAMP_KEY);
      
      if (stored && storedTimestamp) {
        const timestamp = parseInt(storedTimestamp, 10);
        const now = Date.now();
        const age = now - timestamp;
        
        // Only use stored params if they're from the same session (within 24 hours)
        if (age < this.SESSION_DURATION) {
          this.utmParams = JSON.parse(stored);
        } else {
          // Stale params - clear them
          localStorage.removeItem(this.STORAGE_KEY);
          localStorage.removeItem(this.TIMESTAMP_KEY);
          this.utmParams = {};
        }
      }
    }
  }

  private extractTrackingParams(params: Record<string, string>) {
    const utmData: Record<string, string> = {};
    for (const key in params) {
      // Capture UTM parameters (utm_source, utm_medium, etc.)
      if (key.startsWith('utm_')) {
        utmData[key] = params[key];
      }
      // Capture Google Ads parameters (gclid, gad_source, gad_campaignid, etc.)
      else if (key === 'gclid' || key.startsWith('gad_')) {
        utmData[key] = params[key];
      }
      // Capture Facebook Click ID (fbclid)
      else if (key === 'fbclid') {
        utmData[key] = params[key];
      }
    }
    return utmData;
  }

  public getUtmParams(): Record<string, string> {
    let rawParams: Record<string, string> = {};
    
    if (isPlatformBrowser(this.platformId)) {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      const storedTimestamp = localStorage.getItem(this.TIMESTAMP_KEY);
      
      if (stored && storedTimestamp) {
        const timestamp = parseInt(storedTimestamp, 10);
        const now = Date.now();
        const age = now - timestamp;
        
        // Only return stored params if they're still valid (within session duration)
        if (age < this.SESSION_DURATION) {
          rawParams = JSON.parse(stored);
        } else {
          // Stale params - clear them
          localStorage.removeItem(this.STORAGE_KEY);
          localStorage.removeItem(this.TIMESTAMP_KEY);
          return {};
        }
      }
    } else {
      rawParams = this.utmParams;
    }
    
    // Convert Google Ads and Facebook parameters to UTM format
    return this.convertToUtmFormat(rawParams);
  }

  /**
   * Key mapping from tracking parameters to UTM format
   */
  private readonly PARAM_MAPPING: Record<string, string> = {
    // Google Ads parameters
    'gad_campaignid': 'utm_campaign',
    'gad_keyword': 'utm_term',
    'gad_creative': 'utm_content',
  };

  /**
   * Source detection: parameters that indicate a specific source
   */
  private readonly SOURCE_INDICATORS: Record<string, { source: string; medium: string }> = {
    // Google Ads indicators
    'gclid': { source: 'Google', medium: 'cpc' },
    'gad_source': { source: 'Google', medium: 'cpc' },
    'gad_campaignid': { source: 'Google', medium: 'cpc' },
    // Facebook indicators
    'fbclid': { source: 'FB', medium: 'paid' },
    // Reddit Ads indicators
    'rdt_cid': { source: 'Reddit', medium: 'paid' },
  };

  /**
   * Source name normalization
   */
  private readonly SOURCE_NORMALIZATION: Record<string, string> = {
    'fb': 'FB',
    'facebook': 'FB',
    'google': 'Google',
    'ig': 'IG',
    'reddit': 'Reddit',
  };

  /**
   * Converts Google Ads and Facebook parameters to UTM format expected by backend
   */
  private convertToUtmFormat(params: Record<string, string>): Record<string, string> {
    const utmParams: Record<string, string> = {};
    
    // First, copy over any existing UTM parameters
    for (const key in params) {
      if (key.startsWith('utm_')) {
        utmParams[key] = params[key];
      }
    }
    
    // Normalize utm_source values (fb -> FB, etc.)
    if (utmParams['utm_source']) {
      const normalized = this.SOURCE_NORMALIZATION[utmParams['utm_source'].toLowerCase()];
      if (normalized) {
        utmParams['utm_source'] = normalized;
      }
    }
    
    // Detect source and medium from tracking parameters
    let detectedSource: string | null = null;
    let detectedMedium: string | null = null;
    
    for (const [paramKey, config] of Object.entries(this.SOURCE_INDICATORS)) {
      if (params[paramKey]) {
        detectedSource = config.source;
        detectedMedium = config.medium;
        break; // Use first match
      }
    }
    
    // Apply detected source and medium if not already set
    if (detectedSource && !utmParams['utm_source']) {
      utmParams['utm_source'] = detectedSource;
    }
    if (detectedMedium && !utmParams['utm_medium']) {
      utmParams['utm_medium'] = detectedMedium;
    }
    
    // Apply parameter mappings
    for (const [sourceKey, targetKey] of Object.entries(this.PARAM_MAPPING)) {
      if (params[sourceKey] && !utmParams[targetKey]) {
        utmParams[targetKey] = params[sourceKey];
      }
    }
    
    return utmParams;
  }
}
